import showToast from '@/Components/Toast/showToastTemplate';
import { useCurrentFile } from '@/hooks/useCurrentFile';
import { get, post } from '@/Utils/API';
import {
  MODAL_CREATE_SHEET_FROM_VIEW,
  TOAST_TEXT_APPLY_FUNCTION_ERROR,
  TOAST_TYPE_ERROR,
  TOAST_TYPE_SUCCESS,
  URL_DATASET,
  URL_DATASETS,
} from '@/Utils/constants';
import { getFileUuidFromPath } from '@/Utils/getFileUuidFromPath';
import { useEffect, useState } from 'react';
import DotsLoader from '../Loader/DotsLoader';
import withModal from '../Modals/withModalHOC';
import { Button } from '../UI/Button/Button';
import { Select } from '../UI/Form/Select/Select';
import TextInput from '../UI/Form/TextInput/TextInput';
import Icon from '../UI/Icon/Icon';
import Modal from '../UI/Modal/Modal';

import { setIsRequestingRows } from '@/redux/reducers/spreadsheetSlice';
import Label from '../UI/Form/Label/Label';

const DEFAULT_FOLDER_HANDLE = 'Library';

const CreateSheetFromView = ({ show, hideModal, view }) => {
  const currentFile = useCurrentFile();

  const { name = '', id = '' } = view || [];

  const [viewName, setViewName] = useState(name);
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);
  const [folderOptions, setFolderOptions] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');

  const { ParentDirectory } = currentFile?.metadata || {};

  const [isLoading, setIsLoading] = useState(false);

  const getOptions = async () => {
    setIsLoadingFolders(true);
    try {
      const files = await get(URL_DATASETS);

      if (!files || files === 'null') {
        setFolderOptions([{ value: DEFAULT_FOLDER_HANDLE, label: DEFAULT_FOLDER_HANDLE }]);
        setSelectedFolder(DEFAULT_FOLDER_HANDLE);
      } else {
        const [defaultFolder] = files.filter((el) => el.FileUuid === ParentDirectory);
        const options = files
          .filter(({ IsDirectory }) => IsDirectory)
          .map((file) => ({ value: file.FileUuid, label: file.FileName }))
          .sort((a, b) => {
            const labelA = a.label.toLowerCase();
            const labelB = b.label.toLowerCase();
            return labelA < labelB ? -1 : labelA > labelB ? 1 : 0;
          });
        setFolderOptions([{ value: DEFAULT_FOLDER_HANDLE, label: 'Library' }, ...options]);
        setSelectedFolder(!defaultFolder ? DEFAULT_FOLDER_HANDLE : defaultFolder.FileUuid);
      }
    } catch (error) {
      showToast({
        type: TOAST_TYPE_ERROR,
        text: TOAST_TEXT_APPLY_FUNCTION_ERROR,
        errorContext: error,
        endpoint: URL_DATASETS,
        fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
      });
    } finally {
      setIsLoadingFolders(false);
    }
  };

  const handleCreateNewSheet = async () => {
    setIsLoading(true);
    let endpoint = '';
    let payload = {};
    try {
      payload = {
        name: viewName,
        parent_directory: selectedFolder === DEFAULT_FOLDER_HANDLE ? '' : selectedFolder,
      };
      endpoint = `${URL_DATASET}/${getFileUuidFromPath()}/views/${id}/as-linked-sheet`;
      const response = await post(endpoint, payload);
      if (response.hasOwnProperty('Success') && !response.Success) {
        throw new Error();
      }
      showToast({
        type: TOAST_TYPE_SUCCESS,
        text: 'Sheet Created!',
        errorContext: null,
      });
    } catch (error) {
      showToast({
        type: TOAST_TYPE_ERROR,
        text: 'Failed to update view. Please try again later or contact support.',
        errorContext: error,
        endpoint: endpoint,
        payload: payload,
        fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
      });
    } finally {
      setIsLoading(false);
      hideModal();
    }
  };

  useEffect(() => {
    if (!show) {
      return;
    } else {
      getOptions();
    }
  }, [show]);

  return (
    <Modal
      size='small'
      modalTitleClasses='!m-0 !p-0'
      title='Create Linked Sheet from View'
      titleSize='small'
      isOpen={show}
      onClose={hideModal}
    >
      <div>
        <div className='flex gap-1 p-2 mb-5 text-sm font-medium border rounded bg-capri-100 bg-opacity-24 border-capri-700 text-capri-700'>
          <Icon name='info' />
          Changes made to this sheet will affect the linked sheet.
        </div>
      </div>
      <div className='mb-5'>
        <TextInput
          label='Name'
          className='w-full mb-5'
          placeholder='View'
          data-cy='lookup-choose-file-search-input'
          value={viewName}
          onChange={(e) => setViewName(e.target.value)}
        />
        {isLoadingFolders ? (
          <div className='flex flex-col gap-2 my-2'>
            <Label text='Folder' />
            <div className='flex items-center justify-between w-full h-8 border rounded border-shadow-200 bg-ui-secondary'>
              <DotsLoader setIsRequestingRows={setIsRequestingRows} />
              <Icon name='caret-down' size={12} className='mr-2' />
            </div>
          </div>
        ) : (
          <Select
            label='Folder'
            value={folderOptions?.find((i) => i.value === selectedFolder)?.value || ''}
            options={folderOptions}
            onChange={(option) =>
              setSelectedFolder(folderOptions?.find((i) => i.value === option)?.value || '')
            }
            placeholder='Select Folder'
          />
        )}
      </div>

      <hr className='my-2' />
      <div className='flex justify-end w-full gap-1'>
        <Button variant='ghost' onClick={hideModal}>
          Cancel
        </Button>
        <Button color='oceanBlue' onClick={handleCreateNewSheet} disabled={isLoading}>
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default withModal({ name: MODAL_CREATE_SHEET_FROM_VIEW })(CreateSheetFromView);
